import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Gift, Lock, Star } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, timeAgo } from "@/lib/api";
import { Avatar } from "@/components/Layout";

const ICON = { subscription: Star, tip: Gift, unlock: Lock };
const VERB = { subscription: "subscribed to you", tip: "sent you a tip", unlock: "unlocked your paid message" };

export default function NotificationsBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    window.addEventListener("riff:notifications-updated", load);
    return () => window.removeEventListener("riff:notifications-updated", load);
  }, [load]);

  const onOpen = (open) => {
    if (open && data.unread) {
      api.post("/notifications/read-all").catch(() => {});
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger data-testid="nav-notifications-btn" aria-label="Notifications"
        className="relative rounded-sm p-2 text-gray-400 outline-none transition-colors hover:bg-white/5 hover:text-white">
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
        {data.unread > 0 && (
          <span data-testid="nav-notifications-badge"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-sm bg-[#FF2E4D] px-1 text-[10px] font-semibold tabular-nums text-white">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-md border-[#2A2E37] bg-[#121418] p-0 text-white" data-testid="notifications-panel">
        <p className="border-b border-[#2A2E37] px-4 py-3 text-sm font-semibold tracking-tight">Notifications</p>
        <div className="max-h-96 divide-y divide-[#2A2E37] overflow-y-auto">
          {data.items.length === 0 && <p className="px-4 py-8 text-center text-xs text-gray-500" data-testid="notifications-empty">New subscribers, tips and unlocks will show up here.</p>}
          {data.items.map((n) => {
            const Icon = ICON[n.type] || Bell;
            return (
              <Link key={n.id} to={`/u/${n.actor.username}`} data-testid="notification-item"
                className={`flex items-start gap-3 px-4 py-3 transition-colors hover:bg-white/5 ${n.read ? "" : "bg-white/[0.03]"}`}>
                <Avatar user={n.actor} size="h-8 w-8" />
                <div className="min-w-0 flex-1 text-[13px] leading-snug">
                  <p><span className="font-semibold">@{n.actor.username}</span> <span className="text-gray-300">{VERB[n.type] || n.text}</span></p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
                    <Icon className="h-3 w-3 text-[#FF2E4D]" />{n.amount ? `$${n.amount.toFixed(2)} · ` : ""}{timeAgo(n.created_at)}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
